import React, { useEffect, useRef, useState } from "react";
import {
  TextField, FormControl, RadioGroup, FormControlLabel, Radio,
  Checkbox, FormGroup, Select, MenuItem, Slider, Typography, Divider, Box, Autocomplete
} from "@mui/material";
import takaokaLogo from "../../../assets/img/takaoka.svg";
import { NATIONALITIES, toNationalityCode } from "../formsUtils";

/** Formata progressivamente um CPF: 000.000.000-00 (só dígitos, máx 11). */
function maskCpf(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
}

/** Formata progressivamente um telefone BR: (00) 00000-0000 (só dígitos, máx 11). */
function maskPhone(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length > 10) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length > 6) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length > 2) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length > 0) return `(${d}`;
  return d;
}

/** Formata progressivamente uma data: dd/mm/aaaa (só dígitos, máx 8). */
export function maskDate(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 8);
  if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return d;
}

/** "YYYY-MM-DD" -> "dd/mm/aaaa". Vazio se não estiver nesse formato. */
export function ymdToBr(v) {
  const m = String(v || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * "dd/mm/aaaa" -> "YYYY-MM-DD". Vazio se incompleto ou impossível (31/02).
 * O que se GRAVA continua sendo YYYY-MM-DD: a máscara é só de exibição, e
 * `engine/expression.js` calcula a idade a partir do valor gravado.
 */
export function brToYmd(br) {
  const d = String(br || "").replace(/\D/g, "");
  if (d.length !== 8) return "";
  const dd = Number(d.slice(0, 2));
  const mm = Number(d.slice(2, 4));
  const yyyy = Number(d.slice(4));
  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return "";
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

/**
 * Campo de data como texto único com máscara.
 *
 * O react-date-picker usava três caixas com auto-avanço: digitar "3" no mês
 * saltava para o ano, e o preenchimento forçava zero à esquerda. A biblioteca
 * não expõe opção para desligar isso.
 *
 * Guarda o texto digitado em estado próprio porque, no meio da digitação, a data
 * ainda não é convertível para o formato gravado. `ultimoEmitido` distingue
 * mudança vinda daqui de mudança vinda de fora (carga de resposta salva).
 */
function DateField({ value, onChange, onInvalid, error, large, ariaLabel }) {
  const [texto, setTexto] = useState(() => ymdToBr(value));
  const ultimoEmitido = useRef(value);

  useEffect(() => {
    if (value !== ultimoEmitido.current) {
      setTexto(ymdToBr(value));
      ultimoEmitido.current = value;
    }
  }, [value]);

  const digitos = texto.replace(/\D/g, "").length;
  const naoConvertida = texto.trim() !== "" && !brToYmd(texto);
  // O aviso só aparece com a data inteira digitada, mas o bloqueio vale desde o
  // primeiro dígito: "12/11/24" grava "" e sem isso o envio sairia sem a data.
  const dataInvalida = digitos === 8 && naoConvertida;

  useEffect(() => {
    if (onInvalid) onInvalid(naoConvertida ? "Data inválida" : "");
  }, [naoConvertida, onInvalid]);

  const handleChange = (raw) => {
    const mascarado = maskDate(raw);
    setTexto(mascarado);
    const ymd = brToYmd(mascarado);
    ultimoEmitido.current = ymd;
    onChange(ymd);
  };

  return (
    <>
      <TextField fullWidth variant="outlined"
        placeholder="dd/mm/aaaa"
        InputProps={{ sx: { fontSize: large ? "1.2rem" : undefined } }}
        inputProps={{ inputMode: "numeric", maxLength: 10, "aria-label": ariaLabel }}
        value={texto}
        onChange={(e) => handleChange(e.target.value)}
        error={!!error || dataInvalida}
        autoFocus={large} />
      {dataInvalida && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          Data inválida
        </Typography>
      )}
    </>
  );
}

/**
 * Faixas plausíveis das medidas clínicas, na unidade que o cálculo do IMC
 * espera. `cm` só existe para a altura: 0,3–2,5 m e 30–250 cm não se encostam,
 * então "180" só pode ser centímetro e não há o que adivinhar.
 */
const MEDIDAS = {
  height: {
    unidade: "m", min: 0.3, max: 2.5, cm: [30, 250],
    aviso: "Informe a altura entre 0,30 e 2,50 m — ou em centímetros, como 180",
  },
  weight: {
    unidade: "kg", min: 0.5, max: 400,
    aviso: "Informe o peso em quilos, entre 0,5 e 400",
  },
};

/** Aceita vírgula ou ponto, um separador só, no máximo duas casas decimais. */
const NENHUM_DOS_ACIMA = "Nenhum dos acima";

/**
 * Marca/desmarca uma opção de múltipla escolha. "Nenhum dos acima" é exclusivo: junto com
 * outra opção o laudo dizia ao mesmo tempo que o paciente tem e não tem a condição.
 */
export function alternarOpcao(selecionadas, opcao) {
  if (selecionadas.includes(opcao)) return selecionadas.filter((x) => x !== opcao);
  if (opcao === NENHUM_DOS_ACIMA) return [opcao];
  return [...selecionadas.filter((x) => x !== NENHUM_DOS_ACIMA), opcao];
}

export function maskDecimal(v) {
  const limpo = String(v == null ? "" : v).replace(/[^\d.,]/g, "");
  const i = limpo.search(/[.,]/);
  if (i === -1) return limpo;
  const decimais = limpo.slice(i + 1).replace(/[.,]/g, "").slice(0, 2);
  return limpo.slice(0, i) + limpo[i] + decimais;
}

/**
 * Converte a medida digitada para a unidade do cálculo: altura em metros, peso
 * em quilos. Devolve "" quando o número não é plausível.
 *
 * A conversão de cm duplica o que apaScoring e legacyApaSubmission já fazem, de
 * propósito: aqui o paciente vê o que foi entendido antes de enviar.
 *
 * A faixa é o que não existia em lugar nenhum. Com `<input type="number">` uma
 * altura de "12" era aceita, e do outro lado 12 > 3 virava 0,12 m — IMC 4861 e
 * risco 3 para quem só errou um dígito.
 */
export function normalizeMeasure(clinicalKey, texto) {
  const n = Number(String(texto == null ? "" : texto).replace(",", "."));
  if (!isFinite(n) || n <= 0) return "";
  const faixa = MEDIDAS[clinicalKey];
  if (!faixa) return String(n);
  if (n >= faixa.min && n <= faixa.max) return String(n);
  if (faixa.cm && n >= faixa.cm[0] && n <= faixa.cm[1]) return String(n / 100);
  return "";
}

function paraExibicao(valor) {
  return valor === "" || valor == null ? "" : String(valor).replace(".", ",");
}

function MeasureField({ field, value, onChange, onInvalid, error, large }) {
  const faixa = MEDIDAS[field.clinicalKey];
  const [texto, setTexto] = useState(() => paraExibicao(value));
  const ultimoEmitido = useRef(value);

  useEffect(() => {
    if (value !== ultimoEmitido.current) {
      setTexto(paraExibicao(value));
      ultimoEmitido.current = value;
    }
  }, [value]);

  const normalizado = normalizeMeasure(field.clinicalKey, texto);
  const foraDaFaixa = texto.trim() !== "" && normalizado === "";
  const convertido =
    normalizado !== "" && Number(normalizado) !== Number(texto.replace(",", "."));

  useEffect(() => {
    if (onInvalid) onInvalid(foraDaFaixa ? faixa.aviso : "");
  }, [foraDaFaixa, faixa, onInvalid]);

  const handleChange = (bruto) => {
    const mascarado = maskDecimal(bruto);
    setTexto(mascarado);
    const valor = normalizeMeasure(field.clinicalKey, mascarado);
    ultimoEmitido.current = valor;
    onChange(valor);
  };

  return (
    <>
      <TextField fullWidth variant="outlined"
        placeholder={field.placeholder || (field.clinicalKey === "height" ? "1,80" : "70,5")}
        InputProps={{
          sx: { fontSize: large ? "1.2rem" : undefined },
          endAdornment: <Typography color="text.secondary">{faixa.unidade}</Typography>,
        }}
        inputProps={{ inputMode: "decimal", maxLength: 6, "aria-label": field.label }}
        value={texto}
        onChange={(e) => handleChange(e.target.value)}
        error={!!error || foraDaFaixa}
        autoFocus={large} />
      {foraDaFaixa && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          {faixa.aviso}
        </Typography>
      )}
      {convertido && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Entendido como {paraExibicao(normalizado)} {faixa.unidade}
        </Typography>
      )}
    </>
  );
}

/** Renderiza um único campo do formulário (compartilhado entre os renderizadores). */
export default function FieldInput({ field, value, onChange, error, required, large, onInvalid }) {
  const labelVariant = large ? "h5" : "body1";
  const label = (
    <Typography variant={labelVariant} component="div" sx={{ mb: large ? 2.25 : 0.5, fontWeight: large ? 500 : 400, fontSize: large ? "1.75rem" : undefined }}>
      {field.label} {required ? <span style={{ color: "#d32f2f" }}>*</span> : null}
    </Typography>
  );

  const help = field.helpText ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
      {field.helpText}
    </Typography>
  ) : null;

  const errText = error ? (
    <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>{error}</Typography>
  ) : null;

  if (field.type === "number" && MEDIDAS[field.clinicalKey]) {
    return (
      <Box sx={{ my: 1.5 }}>
        {label}{help}
        <MeasureField field={field} value={value} onChange={onChange} onInvalid={onInvalid} error={error} large={large} />
        {errText}
      </Box>
    );
  }

  // Layout das opções (radio/checkbox/yesno): grade responsiva + opção em "cartão"
  const optionsCount = (field.options || []).length;
  const groupSx = {
    display: "grid",
    // várias opções curtas → 2 colunas em telas médias; 1 coluna no celular
    gridTemplateColumns: optionsCount > 1 ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
    gap: 1,
    mt: 1,
  };
  const optionSx = (selected) => ({
    m: 0,
    border: "1px solid",
    borderColor: selected ? "primary.main" : "divider",
    bgcolor: selected ? "action.selected" : "transparent",
    borderRadius: 1.5,
    px: 1.5,
    py: large ? 1 : 0.5,
    alignItems: "center",
    transition: "border-color .15s, background-color .15s",
    "&:hover": { borderColor: "primary.light", bgcolor: "action.hover" },
    "& .MuiFormControlLabel-label": { fontSize: large ? "1.2rem" : "0.95rem", lineHeight: 1.35 },
  });

  switch (field.type) {
    case "section":
      return (
        <Box sx={{ mt: large ? 0 : 3, mb: 1 }}>
          <Typography variant={large ? "h5" : "subtitle1"}>{field.label}</Typography>
          <Divider />
        </Box>
      );
    case "paragraph":
      return (
        <Box sx={{ my: 1.5, textAlign: field.body ? "center" : "left" }}>
          <Typography variant={large ? "h5" : "body1"} sx={{ fontWeight: field.body ? 700 : 400, color: "#13335f" }}>
            {field.label}
          </Typography>
          {field.body && (
            <Typography variant="body2" sx={{ mt: 1, color: "#333", whiteSpace: "pre-line", fontStyle: "italic" }}>
              {field.body}
            </Typography>
          )}
        </Box>
      );

    case "cover":
      return (
        <Box sx={{ textAlign: "center", mt: 0, mb: 1 }}>
          <Box component="img" src={takaokaLogo} alt="Takaoka"
            sx={{ height: large ? 84 : 56, width: "auto", objectFit: "contain", mb: large ? 3 : 2 }} />
          <Typography sx={{
            color: "#13335f", fontWeight: 700, lineHeight: 1.2,
            fontSize: large ? "2.3rem" : "1.4rem", mb: large ? 4 : 3,
          }}>
            {field.label}
          </Typography>
          {field.body && (
            <Typography sx={{
              color: "#555", whiteSpace: "pre-line",
              fontSize: large ? "1.2rem" : "1rem", lineHeight: 1.6,
            }}>
              {field.body}
            </Typography>
          )}
        </Box>
      );

    case "consent":
      return (
        <Box sx={{ my: 1, textAlign: "center" }}>
          <Typography variant={large ? "h5" : "subtitle1"}
            sx={{ fontWeight: 600, mb: 1.5, fontSize: large ? "1.75rem" : undefined }}>
            {field.label} {required ? <span style={{ color: "#d32f2f" }}>*</span> : null}
          </Typography>
          {field.body && (
            <Typography variant="body2" sx={{
              color: "#555", whiteSpace: "pre-line", mb: 2,
              fontSize: large ? "1.15rem" : undefined, lineHeight: 1.6,
              maxHeight: large ? 380 : 200, overflow: "auto",
            }}>
              {field.body}
            </Typography>
          )}
          <FormControl error={!!error} component="fieldset" fullWidth>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox sx={{ p: 0, mr: 1 }} checked={value === "aceito"}
                    onChange={(e) => onChange(e.target.checked ? "aceito" : "")} />
                }
                label={
                  <Typography variant="body2" sx={{ userSelect: "none", fontSize: large ? "1.2rem" : undefined }}>
                    {field.checkboxLabel || "Concordo"} {required ? <span style={{ color: "#d32f2f" }}>*</span> : null}
                  </Typography>
                }
              />
            </Box>
          </FormControl>
          {errText}
        </Box>
      );

    case "textarea":
      return (
        <Box sx={{ my: 1.5 }}>
          {label}{help}
          <TextField fullWidth multiline rows={large ? 4 : 3} variant="outlined" placeholder={field.placeholder}
            InputProps={{ sx: { fontSize: large ? "1.2rem" : undefined } }}
            inputProps={{ "aria-label": field.label }}
            value={value || ""} onChange={(e) => onChange(e.target.value)} error={!!error} autoFocus={large} />
          {errText}
        </Box>
      );

    case "date":
      return (
        <Box sx={{ my: 1.5 }}>
          {label}{help}
          <DateField
            value={value}
            onChange={onChange}
            onInvalid={onInvalid}
            error={error}
            large={large}
            ariaLabel={field.label} />
          {errText}
        </Box>
      );

    case "number":
    case "text":
    case "email":
    case "phone": {
      const ck = field.clinicalKey;
      const mask = ck === "cpf" ? maskCpf : ck === "phone" ? maskPhone : null;
      const maskPh = ck === "cpf" ? "000.000.000-00" : ck === "phone" ? "(00) 00000-0000" : field.placeholder;
      const maskLen = ck === "cpf" ? 14 : ck === "phone" ? 15 : undefined;
      const foreignEnabled = ck === "cpf" && field.allowForeignCpf;
      // quando "Estrangeiro", o valor é um objeto { foreign:true, doc, nationality }
      const foreignVal = value && typeof value === "object" && value.foreign ? value : null;
      const isForeign = !!foreignVal;
      const foreignLabel = field.foreignLabel || "Por favor, digite o seu RNE / Passaporte";
      // Resposta começada antes de a lista gravar sigla guarda o nome do país. Aqui é só a
      // exibição; o que vai para o D2I é convertido no envio, em `normalizeNationalities`.
      const siglaDoPais = foreignVal ? toNationalityCode(foreignVal.nationality) : "";
      const foreignPh = field.foreignPlaceholder || "Digite o RNE / Passaporte";
      return (
        <Box sx={{ my: 1.5 }}>
          {isForeign ? (
            <>
              <Typography variant={labelVariant} component="div"
                sx={{ mb: large ? 2.25 : 0.5, fontWeight: large ? 500 : 400, fontSize: large ? "1.75rem" : undefined }}>
                {foreignLabel} {required ? <span style={{ color: "#d32f2f" }}>*</span> : null}
              </Typography>
              {help}
              <TextField fullWidth variant="outlined" placeholder={foreignPh}
                InputProps={{ sx: { fontSize: large ? "1.2rem" : undefined } }}
                inputProps={{ "aria-label": foreignLabel }}
                value={foreignVal.doc || ""}
                onChange={(e) => onChange({ ...foreignVal, doc: e.target.value })}
                error={!!error} autoFocus={large} />
              <FormControl fullWidth sx={{ mt: 1.5 }}>
                <Autocomplete
                  options={NATIONALITIES} autoHighlight
                  getOptionLabel={(o) => o.label}
                  value={NATIONALITIES.find((n) => n.value === siglaDoPais) || null}
                  onChange={(e, opcao) => onChange({ ...foreignVal, nationality: opcao ? opcao.value : "" })}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Selecione a nacionalidade" error={!!error}
                      InputProps={{ ...params.InputProps, sx: { fontSize: large ? "1.2rem" : undefined } }}
                      inputProps={{ ...params.inputProps, "aria-label": "Nacionalidade" }} />
                  )}
                />
              </FormControl>
            </>
          ) : (
            <>
              {label}{help}
              <TextField fullWidth variant="outlined"
                placeholder={maskPh}
                type={mask ? "text" : field.type === "number" ? "number" : "text"}
                InputProps={{ sx: { fontSize: large ? "1.2rem" : undefined } }}
                inputProps={{ ...(mask ? { inputMode: "numeric", maxLength: maskLen } : {}), "aria-label": field.label }}
                value={mask ? mask(value) : (value || "")}
                onChange={(e) => onChange(mask ? mask(e.target.value) : e.target.value)}
                error={!!error} autoFocus={large} />
            </>
          )}
          {foreignEnabled && (
            <FormControlLabel sx={{ mt: 0.5 }}
              control={
                <Checkbox size="small" checked={isForeign}
                  onChange={(e) => onChange(e.target.checked ? { foreign: true, doc: "", nationality: "" } : "")} />
              }
              label="Estrangeiro" />
          )}
          {errText}
        </Box>
      );
    }

    case "radio":
    case "yesno":
      return (
        <Box sx={{ my: 1.5 }}>
          <FormControl error={!!error} component="fieldset" fullWidth>
            {label}{help}
            <RadioGroup value={value || ""} onChange={(e) => onChange(e.target.value)} sx={groupSx}>
              {(field.options || []).map((opt) => (
                <FormControlLabel key={opt.value} value={opt.value} control={<Radio />}
                  label={opt.label} sx={optionSx(value === opt.value)} />
              ))}
            </RadioGroup>
          </FormControl>
          {errText}
        </Box>
      );

    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (v) => onChange(alternarOpcao(arr, v));
      return (
        <Box sx={{ my: 1.5 }}>
          <FormControl error={!!error} component="fieldset" fullWidth>
            {label}{help}
            <FormGroup sx={groupSx}>
              {(field.options || []).map((opt) => (
                <FormControlLabel key={opt.value}
                  control={<Checkbox checked={arr.includes(opt.value)} onChange={() => toggle(opt.value)} />}
                  label={opt.label} sx={optionSx(arr.includes(opt.value))} />
              ))}
            </FormGroup>
          </FormControl>
          {errText}
        </Box>
      );
    }

    case "select":
      return (
        <Box sx={{ my: 1.5 }}>
          <FormControl fullWidth error={!!error}>
            {label}{help}
            <Select value={value || ""} onChange={(e) => onChange(e.target.value)} displayEmpty>
              <MenuItem value=""><em>Selecione...</em></MenuItem>
              {(field.options || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {errText}
        </Box>
      );

    case "scale":
      return (
        <Box sx={{ my: 1.5 }}>
          {label}{help}
          <Box sx={{ px: 1, pt: 1 }}>
            <Slider value={typeof value === "number" ? value : 0}
              onChange={(_, v) => onChange(v)} step={1} min={0} max={10} marks valueLabelDisplay="auto" />
          </Box>
          {errText}
        </Box>
      );

    default:
      return null;
  }
}
